import { BusinessPremises }        from '@app/modules/business-premises/models/business-premises.model';
import {
	Ers,
	ErsVariant
}                                  from '@app/modules/ers/models/ers.model';
import { FccDatacenter }           from '@app/modules/fcc/fcc-datacenter/models/fcc-datacenter.model';
import { FccInstallationTypeEnum } from '@app/modules/fcc/models/fcc.model';
import { TssManufacturer }         from '@app/modules/tss/components/tss-manufacturer/tss-manufacturer.model';
import { CspAccount }              from '@app/modules/tss/models/csp.model';
import { TssEntityConfig }         from '@app/modules/tss/presets/tss-entity-config.model';
import { RequestState }            from '@core/interfaces/api.interface';
import { ITssEntityEvents }        from '@core/interfaces/entity.interface';
import {
	EntityActionEventEnum,
	EntityVariantEnum
}                                  from '@models/entity.model';

// ENUMS #############################################################
// API enums *********************************************************
export enum TssAssignmentTypeEnum {
	BUSINESS_PREMISES = 'businessPremises',
	ERS = 'ers'
}

export enum TssDeviceTypeEnum {
	CLOUD = 'cloud',
	MICRO_SD = 'microSd',
	SD = 'sd',
	USB = 'usb'
}

export enum TssManufacturerEnum {
	CRYPTOVISION = 'cryptovision',
	D_TRUST = 'dTrust',
	SWISSBIT = 'swissbit'
}

export enum TssModeEnum {
	CLOUD = 'cloud',
	TOKEN = 'token',
	OFFLINE = 'offline',
	SWISSBIT_TOKEN = 'swissbitToken'
}

export enum TssModeSubTypeEnum {
	CV1 = 'cv1',
	CV2 = 'cv2',
	NO_SUBTYPE = 'noSubtype' // ui exclusive
}

export enum TssModeWithSubTypeEnum {
	CLOUD = 'cloud',
	TOKEN = 'token',
	TOKEN_CV1 = 'tokenCv1',
	TOKEN_CV2 = 'tokenCv2',
	OFFLINE = 'offline',
	SWISSBIT_TOKEN = 'swissbitToken'
}

export enum TssSourceTypeEnum {
	NEW = 'new',
	EXISTING = 'existing'
}

export enum TssStatusEnum {
	ACTIVE = 'active',
	DELETED = 'deleted',
	DISABLED = 'disabled',
	INACTIVE = 'inactive',
	SUSPENDED = 'suspended'
}

export enum TssExternalStatusEnum {
	ACTIVE = 'active',
	DELETED = 'deleted',
	DISABLED = 'disabled',
	INACTIVE = 'inactive'
}


// UI enums *********************************************************
export enum TssInfoActionEnum {
	RENEW_FCC_DATACENTER_API_KEY = 'renewFccDatacenterApiKey',
	RENEW_FCC_SECRET = 'renewFccSecret',
	REVEAL_FCC_SECRET = 'revealFccSecret',
	REVEAL_PUK = 'revealPuk'
}

export enum TssInfoLineIdentifierEnum {
	ACTIVATION_TOKEN = 'activationToken',
	ERS_SECRET = 'ersSecret',
	FCC_DATACENTER_API_KEY = 'fccDatacenterApiKey',
	FCC_DATACENTER_URL = 'fccDatacenterUrl',
	FCC_ID = 'fccId',
	PASSWORD = 'password',
	PUK = 'puk'
}

export enum TssPresetEnum {
	BUSINESS_PREMISES_DETAIL_TSS_EXTERNAL = 'businessPremisesDetailTssExternal',
	TSS = 'tss',
	TSS_EXTERNAL = 'tssExternal',
	TASK_CENTER_FCC_OPEN_TRANSACTIONS = 'taskCenterFccOpenTransactions',
	TASK_CENTER_FCC_VERSIONS = 'taskCenterFccVersions',
}

export enum TssTagEnum {
	FCC_DATACENTER = 'fccDatacenter',
	MODE = 'mode'
}

export enum TssVersionEnum {
	EXTERNAL = 'tssExternal',
	STANDARD = 'tssStandard'
}

// INTERFACES ########################################################
// API interfaces ****************************************************
export interface IActivationTokens {
	activationToken: string;
}

export interface ITssRequestDataBase {
	tssManufacturer: TssManufacturer;
}

export interface ITssAllRequest extends ITssRequestDataBase {
	businessPremisesId?: string;
	ersId?: string;
	firstNamePart: string;
	secondNamePart: string;
}

export interface ITssRequestDataForBusinessPremises extends ITssRequestDataBase {
	businessPremises: BusinessPremises;
}

export interface ITssRequestDataForErs extends ITssRequestDataBase {
	ers: Ers;
}

export interface ITssAllRequestDataForBusinessPremises extends ITssAllRequest {
	businessPremisesId: string;
}

export interface ITssAllRequestDataForErs extends ITssAllRequest {
	ersId: string;
}


// UI interfaces *****************************************************
export interface ITssInfoAction {
	action: TssInfoActionEnum;
	disabled?: boolean;
	hidden?: boolean;
	label: string;
	props?: any;
}

export interface ITssRequestServiceResultSet {
	bp?: BusinessPremises;
	ers?: ErsVariant;
	requestState: RequestState;
	tss?: Tss;
}

export interface IBatchTssRequestResultSet {
	failList: Array<BusinessPremises | ErsVariant>;
	successList: Array<BusinessPremises | ErsVariant>;
}

export interface ITssEntityActionEvent {
	action: EntityActionEventEnum;
	tss: TssVariant;
	tssEntityConfig: TssEntityConfig;
}


// TYPES #############################################################
export type TssProp = ( keyof Tss );
export type TssProps = TssProp[];
export type TssExternalProp = ( keyof TssExternal );
export type TssExternalProps = TssExternalProp[];
export type TssVariantProp = TssProp | TssExternalProp;
export type TssVariantProps = TssProps | TssExternalProps;

export type TssVariant = Tss | TssExternal;
export type TssStatus = TssExternalStatusEnum | TssStatusEnum;


// ENTITIES ##########################################################
export class TssBaseTMO {
	public assignmentType?: TssAssignmentTypeEnum = undefined;
	public bsiId?: string = undefined;
	public businessPremisesId?: string = undefined;
	public createdBy?: string = undefined;
	public createdDate?: Date = undefined;
	public deviceType?: TssDeviceTypeEnum = undefined;
	public id: string = undefined;
	public lastModifiedBy?: string = undefined;
	public lastModifiedDate?: Date = undefined;
	public name?: string = undefined;
	public organizationId?: string = undefined;
	public status?: TssStatus = undefined;
	public tssSerialNumber?: string = undefined;
}


export class TssTMO extends TssBaseTMO {
	public activationDate?: Date = undefined;
	public adminPuk?: string = undefined;
	public cspAccount?: CspAccount = new CspAccount();
	public deactivationDate?: Date = undefined;
	public ersSecret?: string = undefined;
	public fccDataCenter?: FccDatacenter = new FccDatacenter();
	public fccId?: string = undefined;
	public fccInfo?: VirtualTssMetadataTMO = undefined;
	public fccInstallationType?: FccInstallationTypeEnum = undefined;
	public fccProperties?: { [ key: string ]: string; } = undefined;
	public fccSecret?: string = undefined;
	public fccVersion?: string = undefined;
	public finalUploadReceivedDate?: Date = undefined;
	public hwSerialNumberHex?: string = undefined;
	public mode?: TssModeEnum = undefined;
	public modeSubtype?: TssModeSubTypeEnum | string = undefined;
	public status?: TssStatusEnum = undefined;
	public versionUpdateDate?: Date = undefined;
}

export class TssExternalTMO extends TssBaseTMO {
	public assignmentType?: TssAssignmentTypeEnum = TssAssignmentTypeEnum.BUSINESS_PREMISES;
	public status?: TssExternalStatusEnum = undefined;
}

export class TssBodyTMO {
	public ersSecret?: string = undefined;
	public fccProperties?: { [ key: string ]: string; } = undefined;
	public name?: string = undefined;
}

export class TssStatusTMO {
	public status?: TssStatusEnum = undefined;
}

export class TssFccVersionsTMO {
	public versionModeCounts?: TssFccVersionsCount[] = undefined;
	public totalCount?: number = undefined;
}

export class TssFccVersionsCountTMO {
	public count?: number = undefined;
	public counts?: TssFccVersionsModeCount[] = undefined;
	public fccVersion?: string = undefined;
}

export class TssFccVersionsModeCountTMO {
	public count?: number = undefined;
	public mode?: TssModeEnum = undefined;
	public subtypes?: TssFccVersionsSubtypeModeCount[] = undefined;
}

export class TssFccVersionsSubtypeModeCountTMO {
	public count?: number = undefined;
	public modeSubtype?: TssModeSubTypeEnum = undefined;
}


export class TssStatusStatisticsTMO {
	public active?: number = undefined;
	public disabled?: number = undefined;
	public inactive?: number = undefined;
	public suspended?: number = undefined;
}

export class TssModeStatisticsTMO {
	public cloud?: number = undefined;
	public offline?: number = undefined;
	public swissbitToken?: number = undefined;
	public token?: number = undefined;
}

export class VirtualTssMetadataTMO {
	public installationId?: string = undefined;
	public ipAddress?: string = undefined;
	public hostname?: string = undefined;
	public port?: number = undefined;
	public protocol?: string = undefined;
}

export class TssRequestTMO {
	public createCspAccount?: boolean = undefined;
	public ersSecret?: string = undefined;
	public fccInstallationType?: FccInstallationTypeEnum = undefined;
	public mode: TssModeEnum = undefined;
	public modeSubtype?: string = undefined;
	public name: string = undefined;
	public triggerFccDataCenterInstallation?: boolean = undefined;
}


// MODEL USED IN APPLICATION (with additional properties)
export class Tss extends TssTMO {
	public activationToken?: string = undefined;
	public readonly entityVariant?: EntityVariantEnum = EntityVariantEnum.INTERNAL;
	public etag?: string = undefined;
	public orderIndex?: number = undefined;
	public showPlainEntity?: boolean = undefined;
	public ui?: ITssEntityEvents = {
		entityActions: {}
	};
}

export class TssExternal extends TssExternalTMO {
	public readonly entityVariant?: EntityVariantEnum = EntityVariantEnum.EXTERNAL;
	public etag?: string = undefined;
	public orderIndex?: number = undefined;
	public showPlainEntity?: boolean = undefined;
	public ui?: ITssEntityEvents = {
		entityActions: {}
	};
}

export class TssStatistics {
	public tssModeStatistics?: TssModeStatistics = undefined;
	public tssStatusStatistics?: TssStatusStatistics = undefined;
}

export class TssFccVersions extends TssFccVersionsTMO {}

export class TssFccVersionsCount extends TssFccVersionsCountTMO {}

export class TssFccVersionsModeCount extends TssFccVersionsModeCountTMO {}

export class TssFccVersionsSubtypeModeCount extends TssFccVersionsSubtypeModeCountTMO {}

export class TssStatusStatistics extends TssStatusStatisticsTMO {}

export class TssModeStatistics extends TssModeStatisticsTMO {}

export class TssRequest extends TssRequestTMO {
	businessPremisesId?: string = undefined;
	ersId?: string = undefined;
}
